import { useMemo } from 'react';
import { useGetPostByIdAndApiKeyQuery } from '@/redux/post/post.api.slice';
import NotFound from '../NotFound';
import useSearchParamActions from '@/customHooks/useSearchParamActions';
import ShareButton from '@/components/Post/ShareButton';
import DownloadPostAttachementsModal from '@/redux/post/DownloadPostAttachementsModal';
import PostDetailBody from '../PostDetail/PostDetailBody';
import Loader from '@/components/common/Loader';
import Notice from '@/components/ui/notice';
import ReviewHistorySheet from '@/components/ReviewHistory/ReviewHistorySheet';

/**
 * Admin preview of a single project, opened from the approvals queue with a
 * short-lived token.
 *
 * Renders `PostDetailBody` — the same body the public detail page uses — so a
 * reviewer sees what the maker will see. The token payload has no requester, so
 * there is no `hasLikedByUser` / `hasBookmarkedByUser` to drive the like, rate
 * and bookmark controls; only Share and Download are passed in.
 */
// Count is presentational gating (token guards, empty admin comments), not logic.
// fallow-ignore-next-line complexity
function PreviewModalDetail() {
  const { getSearchParam } = useSearchParamActions();
  const token = getSearchParam('token');
  const postId = getSearchParam('postId');

  const {
    data: post,
    isLoading,
    refetch,
  } = useGetPostByIdAndApiKeyQuery({
    token: token || '',
    postId: postId || '',
  });

  const adminComments = useMemo(() => {
    return post?.adminComments || [];
  }, [post]);

  if (isLoading) return <Loader title="Just a moment! 😊" description="" />;

  if (!post || !token || !postId) {
    return <NotFound />;
  }

  return (
    <div
      className="scroll-snap-type-y-mandatory pb-24"
      style={{ marginTop: 'var(--nav-height)' }}
    >
      {post.visibility !== 'public' && (
        <Notice className="mx-5 mt-4">
          This project is {post.visibility} and not yet visible to others.
        </Notice>
      )}

      <PostDetailBody
        post={post}
        notice={
          <ReviewHistorySheet
            target={{ kind: 'post', id: postId }}
            token={token}
            comments={adminComments}
            onRefetch={refetch}
          />
        }
        interactions={
          <ShareButton
            shareUrl={`${window.location.origin}/model/${postId}`}
            size="md"
          />
        }
        download={
          <DownloadPostAttachementsModal
            attachments={post?.attachments}
            postTitle={post.title}
            licenceType={post?.licenceType}
            refetch={refetch}
            downloadCount={post.downloadCount ?? 0}
          />
        }
      />
    </div>
  );
}

export default PreviewModalDetail;
